import { useState, useEffect } from 'react';
import { Badge, Modal } from 'react-bootstrap';
import ScriptEditor from '../ui/ScriptEditor';
import Button from 'components/base/Button';
import api from 'services/api';

interface PythonLibrary {
  python_library_id: number;
  name: string;
  filename: string;
  description?: string;
  content?: string;
}

const NorscomPython = ({ state, onChange, readOnly = false }: any) => {
  const { script } = state.config || {};
  const nodeDefinitionId = state.node_definition_id;

  const [linkedLibraries, setLinkedLibraries] = useState<PythonLibrary[]>([]);
  const [loadingLibraries, setLoadingLibraries] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedLib, setSelectedLib] = useState<PythonLibrary | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!nodeDefinitionId) return;
    setLoadingLibraries(true);
    api
      .get(`/node_definitions/${nodeDefinitionId}/python_libraries`)
      .then((res) => setLinkedLibraries(res.data || []))
      .catch((err) => console.error('Failed to fetch linked python libraries', err))
      .finally(() => setLoadingLibraries(false));
  }, [nodeDefinitionId]);

  const handleOpenModal = () => {
    setSelectedLib(null);
    setSearch('');
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedLib(null);
    setSearch('');
  };

  const handleSelectLib = async (lib: PythonLibrary) => {
    if (lib.content) {
      setSelectedLib(lib);
      return;
    }
    setLoadingContent(true);
    try {
      const res = await api.get(`/python_libraries/${lib.python_library_id}`);
      setSelectedLib(res.data);
      setLinkedLibraries((prev) => prev.map((l) => (l.python_library_id === lib.python_library_id ? res.data : l)));
    } catch (err) {
      console.error('Failed to fetch library content', err);
    } finally {
      setLoadingContent(false);
    }
  };

  const filtered = linkedLibraries.filter((lib) => {
    const q = search.toLowerCase();
    return lib.name.toLowerCase().includes(q) || lib.filename.toLowerCase().includes(q) || (lib.description || '').toLowerCase().includes(q);
  });

  return (
    <>
      <ScriptEditor
        label="Script"
        controlId="formNode-script"
        name="script"
        value={script || ''}
        language="python"
        onChange={(e) => onChange(e, true)}
        readOnly={readOnly}
        aria-label="Python script editor"
      />

      <div className="mb-3 d-flex align-items-center gap-2">
        <Button variant="phoenix-secondary" size="sm" onClick={handleOpenModal} disabled={!nodeDefinitionId || loadingLibraries}>
          Available Libraries
          {linkedLibraries.length > 0 && (
            <Badge bg="primary" className="ms-2">
              {linkedLibraries.length}
            </Badge>
          )}
        </Button>
        {!nodeDefinitionId && <span className="text-muted small">Save the node definition first to view libraries.</span>}
      </div>

      <Modal show={showModal} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>
            {selectedLib ? (
              <span role="button" className="text-primary me-2" style={{ cursor: 'pointer', fontWeight: 400 }} onClick={() => setSelectedLib(null)}>
                ← Libraries /
              </span>
            ) : null}
            {selectedLib ? selectedLib.name : 'Available Libraries'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedLib ? (
            <>
              <div className="mb-2 d-flex align-items-center gap-3">
                <code className="text-muted small">{selectedLib.filename}</code>
                {selectedLib.description && <span className="text-muted small">{selectedLib.description}</span>}
              </div>
              <pre
                style={{
                  background: '#1e1e1e',
                  color: '#d4d4d4',
                  padding: '1rem',
                  borderRadius: '0.375rem',
                  fontFamily: '"Fira Code", "Fira Mono", monospace',
                  fontSize: 13,
                  overflowX: 'auto',
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  whiteSpace: 'pre',
                  margin: 0,
                }}
              >
                {selectedLib.content || ''}
              </pre>
            </>
          ) : loadingLibraries ? (
            <div className="text-muted small">Loading...</div>
          ) : linkedLibraries.length === 0 ? (
            <div className="text-muted small">
              No libraries linked to this node definition. Go to <strong>Configuration → Python Libraries</strong> to link libraries.
            </div>
          ) : (
            <>
              <input
                type="text"
                className="form-control form-control-sm mb-2"
                placeholder="Search by name, filename or description…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              {filtered.length === 0 ? (
                <div className="text-muted small">No libraries match &quot;{search}&quot;.</div>
              ) : (
                <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                  <tbody>
                    {filtered.map((lib) => (
                      <tr key={lib.python_library_id} role="button" onClick={() => handleSelectLib(lib)} style={{ cursor: 'pointer' }}>
                        <td className="py-1 fw-semibold">{lib.name}</td>
                        <td className="py-1">
                          <code className="text-muted">{lib.filename}</code>
                        </td>
                        <td className="py-1 text-muted">{lib.description || ''}</td>
                        <td className="py-1 text-end text-muted">View →</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
          {loadingContent && <div className="text-center text-muted small mt-2">Loading content…</div>}
        </Modal.Body>

        <Modal.Footer>
          {selectedLib && (
            <Button variant="phoenix-secondary" onClick={() => setSelectedLib(null)}>
              ← Back to list
            </Button>
          )}
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NorscomPython;
